import { Component } from 'react';
import { createRecordingDialogEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import { MEET_FEATURES } from '../../../base/jwt/constants';
import { isJwtFeatureEnabled } from '../../../base/jwt/functions';
import { JitsiRecordingConstants } from '../../../base/lib-jitsi-meet';
import { isLocalParticipantModerator } from '../../../base/participants/functions';
import { updateDropboxToken } from '../../../dropbox/actions';
import { getNewAccessToken, isEnabled as isDropboxEnabled } from '../../../dropbox/functions';
import { getDropboxData } from '../../../dropbox/functions.any';
import { showErrorNotification } from '../../../notifications/actions';
import { setRequestingSubtitles } from '../../../subtitles/actions.any';
import { canAddTranscriber, isRecorderTranscriptionsRunning } from '../../../transcribing/functions';
import { setSelectedRecordingService, setStartRecordingIntent, setStopRecordingIntent, startLocalVideoRecording, stopLocalVideoRecording } from '../../actions.any';
import { RECORDING_METADATA_ID, RECORDING_TYPES } from '../../constants';
import { getActiveSession, hasRecordingOrTranscriptionFeature, isRecordingRunning, isRecordingSharingEnabled, shouldAutoTranscribeOnRecord, supportsLocalRecording } from '../../functions';
import logger from '../../logger';
/**
 * Component for the recording start dialog.
 */
class AbstractStartRecordingDialog extends Component {
    /**
     * Initializes a new {@code StartRecordingDialog} instance.
     *
     * @inheritdoc
     */
    constructor(props) {
        super(props);
        // Bind event handler so it is only bound once for every instance.
        this._onSubmit = this._onSubmit.bind(this);
        this._onSelectedRecordingServiceChanged
            = this._onSelectedRecordingServiceChanged.bind(this);
        this._onSharingSettingChanged = this._onSharingSettingChanged.bind(this);
        this._toggleScreenshotCapture = this._toggleScreenshotCapture.bind(this);
        this._onLocalRecordingSelfChange = this._onLocalRecordingSelfChange.bind(this);
        this._onTranscriptionChange = this._onTranscriptionChange.bind(this);
        this._onRecordAudioAndVideoChange = this._onRecordAudioAndVideoChange.bind(this);
        let selectedRecordingService = '';
        if (this.props._fileRecordingsServiceEnabled) {
            selectedRecordingService = RECORDING_TYPES.JITSI_REC_SERVICE;
        }
        else if (this._areIntegrationsEnabled()) {
            if (props._localRecordingEnabled && supportsLocalRecording()) {
                selectedRecordingService = RECORDING_TYPES.LOCAL;
            }
            else {
                selectedRecordingService = RECORDING_TYPES.DROPBOX;
            }
        }
        else if (props._localRecordingEnabled && supportsLocalRecording()) {
            selectedRecordingService = RECORDING_TYPES.LOCAL;
        }
        // If no service is available, selectedRecordingService stays '' and
        // the Start Recording button will be disabled.
        const recordingRunning = props._recordingRunning ?? false;
        const transcriptionRunning = props._transcriptionRunning ?? false;
        const hasActiveSession = recordingRunning || transcriptionRunning;
        this.state = {
            isTokenValid: false,
            isValidating: false,
            userName: undefined,
            sharingEnabled: true,
            // When a session is active derive initial toggles from running state.
            // Explicit props (nudge flow) take priority.
            shouldRecordAudioAndVideo: hasActiveSession
                ? (props.initialRecording ?? recordingRunning)
                : (props.recordAudioAndVideo ?? false),
            // 录制弹窗不再提供「录制转录」；仅当已有转写会话时保留运行态以便停止
            shouldRecordTranscription: hasActiveSession
                ? (props.initialTranscription ?? transcriptionRunning)
                : false,
            spaceLeft: undefined,
            selectedRecordingService,
            localRecordingOnlySelf: false
        };
    }
    /**
     * Validates the oauth access token.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidMount() {
        if (typeof this.props._token !== 'undefined') {
            this._onTokenUpdated();
        }
    }
    /**
     * Validates the oauth access token.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidUpdate(prevProps) {
        if (this.props._token !== prevProps._token) {
            this._onTokenUpdated();
        }
    }
    /**
     * Returns true if the integrations with third party services are enabled
     * and false otherwise.
     *
     * @returns {boolean} - True if the integrations with third party services
     * are enabled and false otherwise.
     */
    _areIntegrationsEnabled() {
        return this.props._isDropboxEnabled
            && !this.props._recordingRunning
            && !this.props._transcriptionRunning;
    }
    /**
     * Callback to handle sharing setting change from the dialog.
     *
     * @returns {void}
     */
    _onSharingSettingChanged() {
        this.setState({
            sharingEnabled: !this.state.sharingEnabled
        });
    }
    /**
     * Callback to handle local recording only self setting change.
     *
     * @returns {void}
     */
    _onLocalRecordingSelfChange() {
        this.setState({
            localRecordingOnlySelf: !this.state.localRecordingOnlySelf
        });
    }
    /**
     * Handles selected recording service changes.
     *
     * @param {string} selectedRecordingService - The new selected recording
     * service.
     * @returns {void}
     */
    _onSelectedRecordingServiceChanged(selectedRecordingService) {
        this.setState({ selectedRecordingService }, () => {
            this.props.dispatch(setSelectedRecordingService(selectedRecordingService));
        });
    }
    /**
     * Handles transcription switch change.
     *
     * @param {boolean} value - The new value.
     * @returns {void}
     */
    _onTranscriptionChange(value) {
        this.setState({
            shouldRecordTranscription: value
        });
    }
    /**
     * Handles audio and video switch change.
     *
     * @param {boolean} value - The new value.
     * @returns {void}
     */
    _onRecordAudioAndVideoChange(value) {
        this.setState({
            shouldRecordAudioAndVideo: value
        });
    }
    /**
     * Validates the dropbox access token and fetches account information.
     *
     * @returns {void}
     */
    _onTokenUpdated() {
        const { _appKey, _isDropboxEnabled, _token, _rToken, _tokenExpireDate, dispatch } = this.props;
        if (!_isDropboxEnabled) {
            return;
        }
        if (typeof _token === 'undefined') {
            this.setState({
                isTokenValid: false,
                isValidating: false
            });
        }
        else { // @ts-ignore
            if (_tokenExpireDate && Date.now() > new Date(_tokenExpireDate)) {
                getNewAccessToken(_appKey, _rToken)
                    .then((resp) => dispatch(updateDropboxToken(resp.token, resp.rToken, resp.expireDate)));
                return;
            }
            this.setState({
                isTokenValid: false,
                isValidating: true
            });
            getDropboxData(_token, _appKey).then(data => {
                if (typeof data === 'undefined') {
                    this.setState({
                        isTokenValid: false,
                        isValidating: false
                    });
                }
                else {
                    this.setState({
                        isTokenValid: true,
                        isValidating: false,
                        ...data
                    });
                }
            });
        }
    }
    /**
     * Returns true when the current toggle selection differs from what is
     * already running — i.e. there is something to apply.
     *
     * @returns {boolean}
     */
    _isChanged() {
        const { _recordingRunning = false, _transcriptionRunning = false } = this.props;
        return this.state.shouldRecordAudioAndVideo !== _recordingRunning
            || this.state.shouldRecordTranscription !== _transcriptionRunning;
    }
    /**
     * Applies recording/transcription changes by computing the delta between
     * the current running state and the user's selection, then starting or
     * stopping each service accordingly.
     *
     * @returns {boolean|undefined} - True to close the dialog, undefined to
     *   keep it open (e.g. on validation failure).
     */
    _onSubmit() {
        const { _appKey, _conference, _displaySubtitles, _fileRecordingSession, _isDropboxEnabled, _localRecording, _recordingRunning = false, _rToken, _subtitlesLanguage, _token, _transcriptionRunning = false, dispatch } = this.props;
        const { localRecordingOnlySelf, selectedRecordingService, sharingEnabled, shouldRecordAudioAndVideo, shouldRecordTranscription } = this.state;
        const startRecording = !_recordingRunning && shouldRecordAudioAndVideo;
        const stopRecording = _recordingRunning && !shouldRecordAudioAndVideo;
        const startTranscription = !_transcriptionRunning && shouldRecordTranscription;
        const stopTranscription = _transcriptionRunning && !shouldRecordTranscription;
        // Pre-seed intents synchronously — must happen before any async operations
        // so the sound/notification coordinator knows what to wait for.
        if (startRecording || startTranscription) {
            dispatch(setStartRecordingIntent({
                recording: startRecording,
                transcription: startTranscription
            }));
        }
        if ((stopRecording || stopTranscription) && !_localRecording) {
            dispatch(setStopRecordingIntent({
                recording: stopRecording && Boolean(_fileRecordingSession),
                transcription: stopTranscription
            }));
        }
        // === Stop recording ===
        if (stopRecording) {
            sendAnalytics(createRecordingDialogEvent('stop', 'confirm.button'));
            if (_localRecording) {
                dispatch(stopLocalVideoRecording());
            }
            else if (_fileRecordingSession) {
                _conference?.stopRecording(_fileRecordingSession.id);
                this._toggleScreenshotCapture();
                // Keep isTranscribingEnabled in the metadata if transcription is still running,
                // so the metadata listener does not see a false transition on that field.
                _conference?.getMetadataHandler().setMetadata(RECORDING_METADATA_ID, {
                    isRecordingRequested: false,
                    ...(_transcriptionRunning && !stopTranscription && { isTranscribingEnabled: true })
                });
            }
        }
        // === Stop transcription ===
        if (stopTranscription) {
            const recordingStillRunning = _recordingRunning && !stopRecording;
            // When recording is still running, skip the subtitles-internal metadata write and do
            // a single write ourselves. This prevents two consecutive metadata transitions
            // (false→false on isRecordingRequested, then false→true) from firing spurious
            // "recording stopped" and "recording started" sounds and notifications.
            // Spread existing recording metadata so we only change isTranscribingEnabled —
            // hardcoding isRecordingRequested: true would cause a spurious recordingStarting
            // transition if that field was not previously set in the server metadata.
            dispatch(setRequestingSubtitles(false, _displaySubtitles, _subtitlesLanguage, true, false, recordingStillRunning));
            if (recordingStillRunning) {
                const existingRecMeta = _conference?.getMetadataHandler()?.getMetadata()[RECORDING_METADATA_ID] ?? {};
                _conference?.getMetadataHandler().setMetadata(RECORDING_METADATA_ID, {
                    ...existingRecMeta,
                    isTranscribingEnabled: false
                });
            }
        }
        // === Start recording ===
        if (startRecording) {
            let appData;
            const attributes = {};
            switch (selectedRecordingService) {
                case RECORDING_TYPES.DROPBOX: {
                    if (_isDropboxEnabled && _token) {
                        appData = JSON.stringify({
                            'file_recording_metadata': {
                                'upload_credentials': {
                                    'service_name': RECORDING_TYPES.DROPBOX,
                                    'token': _token,
                                    'r_token': _rToken,
                                    'app_key': _appKey
                                }
                            }
                        });
                        attributes.type = RECORDING_TYPES.DROPBOX;
                    }
                    else {
                        dispatch(showErrorNotification({ titleKey: 'dialog.noDropboxToken' }));
                        return;
                    }
                    break;
                }
                case RECORDING_TYPES.JITSI_REC_SERVICE: {
                    appData = JSON.stringify({
                        'file_recording_metadata': {
                            'share': sharingEnabled
                        }
                    });
                    attributes.type = RECORDING_TYPES.JITSI_REC_SERVICE;
                    break;
                }
                case RECORDING_TYPES.LOCAL: {
                    dispatch(startLocalVideoRecording(localRecordingOnlySelf));
                    _conference?.getMetadataHandler().setMetadata(RECORDING_METADATA_ID, {
                        isTranscribingEnabled: shouldRecordTranscription
                    });
                    return true;
                }
            }
            sendAnalytics(createRecordingDialogEvent('start', 'confirm.button', attributes));
            this._toggleScreenshotCapture();
            // IQ 失败时 lib 会 reject 原始 XMPP Element；错误通知由 RECORDER_STATE_CHANGED 展示，
            // 此处必须 catch，否则 webpack-dev-server 会弹出 Uncaught [object Element]。
            _conference?.startRecording({
                mode: JitsiRecordingConstants.mode.FILE,
                appData
            })?.catch((error) => {
                logger.error('开始云端录制失败（Jibri IQ）', error);
            });
        }
        // === Handle transcription start ===
        // JITSI_REC_SERVICE uses setRequestingSubtitles; other services update metadata directly.
        if (startTranscription) {
            if (selectedRecordingService === RECORDING_TYPES.JITSI_REC_SERVICE) {
                dispatch(setRequestingSubtitles(true, _displaySubtitles, _subtitlesLanguage, true, startRecording || _recordingRunning));
            }
            else {
                _conference?.getMetadataHandler().setMetadata(RECORDING_METADATA_ID, {
                    isTranscribingEnabled: true
                });
            }
        }
        else if (startRecording && shouldRecordTranscription
            && selectedRecordingService !== RECORDING_TYPES.JITSI_REC_SERVICE) {
            // Starting recording with transcription already on — ensure metadata is consistent.
            _conference?.getMetadataHandler().setMetadata(RECORDING_METADATA_ID, {
                isRecordingRequested: true,
                isTranscribingEnabled: true
            });
        }
        else if (startRecording) {
            // Recording started without a transcription change: announce it in room metadata so the
            // other participants get the recording start notification/sound (this is what the
            // metadata listener turns into the remote notification). Preserve any existing
            // isTranscribingEnabled so a running transcription is not signalled as stopped.
            const existingRecMeta = _conference?.getMetadataHandler()?.getMetadata()[RECORDING_METADATA_ID] ?? {};
            _conference?.getMetadataHandler().setMetadata(RECORDING_METADATA_ID, {
                ...existingRecMeta,
                isRecordingRequested: true
            });
        }
        return true;
    }
    /**
     * Toggles screenshot capture feature.
     *
     * @returns {void}
     */
    _toggleScreenshotCapture() {
        // To be implemented by subclass.
    }
    /**
     * Renders the platform specific dialog content.
     *
     * @protected
     * @returns {React$Component}
     */
    _renderDialogContent;
}
/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code StartRecordingDialog} component.
 *
 * @param {Object} state - The Redux state.
 * @param {any} _ownProps - Component's own props.
 * @private
 * @returns {IProps}
 */
export function mapStateToProps(state, _ownProps) {
    const { recordingService, dropbox = { appKey: undefined }, localRecording, recordings = { recordAudioAndVideo: true } } = state['features/base/config'];
    const { _displaySubtitles, _language: _subtitlesLanguage } = state['features/subtitles'];
    // Only treat cloud recordings as "running" for users who can actually control them.
    // Non-mods without the recording/transcription JWT feature cannot stop cloud sessions,
    // so from their perspective only their own local recording counts as "running".
    const canManageRecordingOrTranscription = isLocalParticipantModerator(state)
        || hasRecordingOrTranscriptionFeature(state);
    return {
        _appKey: dropbox.appKey ?? '',
        _autoTranscribeOnRecord: shouldAutoTranscribeOnRecord(state),
        _canTranscribe: canAddTranscriber(state),
        _conference: state['features/base/conference'].conference,
        _displaySubtitles,
        _fileRecordingSession: getActiveSession(state, JitsiRecordingConstants.mode.FILE),
        _fileRecordingsServiceEnabled: recordingService?.enabled ?? false,
        _fileRecordingsServiceSharingEnabled: isRecordingSharingEnabled(state),
        _isModerator: isLocalParticipantModerator(state),
        _isDropboxEnabled: isDropboxEnabled(state),
        _localRecording: Boolean(state['features/recording'].localRecordingRunning),
        _localRecordingEnabled: !localRecording?.disable,
        _recordingRunning: canManageRecordingOrTranscription
            ? isRecordingRunning(state)
            : Boolean(state['features/recording'].localRecordingRunning),
        _rToken: state['features/dropbox'].rToken ?? '',
        _transcriptionRunning: canManageRecordingOrTranscription
            ? isRecorderTranscriptionsRunning(state)
            : false,
        recordAudioAndVideo: isJwtFeatureEnabled(state, MEET_FEATURES.RECORDING, false)
            ? _ownProps.recordAudioAndVideo ?? recordings?.recordAudioAndVideo ?? true : false,
        _subtitlesLanguage,
        _tokenExpireDate: state['features/dropbox'].expireDate,
        _token: state['features/dropbox'].token ?? ''
    };
}
export default AbstractStartRecordingDialog;
